import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
/** @type {Set<ProductTitle>} */
const connectedTitles = new Set();

/** @type {ResizeObserver | null} */
const sharedResizeObserver =
  'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        // Batch all reads first, then all writes, to avoid layout thrashing.
        const measured = [];
        for (const entry of entries) {
          const target = /** @type {ProductTitle} */ (entry.target);
          if (!connectedTitles.has(target)) continue;
          measured.push([target, target.measureMaxLines()]);
        }
        for (const [target, maxLines] of measured) {
          if (maxLines != null) target.applyTruncation(maxLines);
        }
      })
    : null;

class ProductTitle extends Component {
  #handleResize = () => {
    const maxLines = this.measureMaxLines();
    if (maxLines != null) this.applyTruncation(maxLines);
  };

  connectedCallback() {
    super.connectedCallback();
    connectedTitles.add(this);

    if (sharedResizeObserver) {
      // observe() delivers an initial notification, which performs the first calculation.
      sharedResizeObserver.observe(this);
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize);
      this.#handleResize();
    }
  }

  /**
   * @returns {HTMLElement}
   */
  get #textElement() {
    return this.refs.text || this.querySelector('.title-text') || this;
  }

  /**
   * Read phase: compute the max number of lines that fit.
   * @returns {number | null}
   */
  measureMaxLines() {
    if (!this.#textElement.textContent) return null;

    const containerHeight = this.clientHeight;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    return Math.max(1, Math.floor(availableHeight / lineHeight));
  }

  /**
   * Write phase: apply the line clamp.
   * @param {number} maxLines
   */
  applyTruncation(maxLines) {
    const textElement = this.#textElement;

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    connectedTitles.delete(this);
    sharedResizeObserver?.unobserve(this);
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
